import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { useApplyFormStore } from '@lib/store';
import type { ApplyCardRequest } from '@shared/api';
import { TitlePage } from './widgets';
import { InsigniaCards } from '@widgets';
import { PageLayout, Button, Modal, Input } from '@shared/widgets';
import bgImg from '@assets/images/eligibility_bg_mobile.png';
import cartImg from '@assets/images/cart.png';

interface IFormData {
  firstName: string;
  lastName: string;
  birthDate: string;
  address: string;
  nationality: string;
  passportIssued: string;
  employmentType: string;
  companyName: string;
  annualIncome?: number | string;
  email: string;
  mobileNumber: string;
  desiredProduct: string;
  jewelleryCardName: string;
}

export const EligibilityFormPage = () => {
  const formControl = useForm({
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      birthDate: '',
      address: '',
      nationality: '',
      passportIssued: '',
      employmentType: '',
      companyName: '',
      annualIncome: '',
      email: '',
      mobileNumber: '',
      desiredProduct: '',
      jewelleryCardName: '',
    } as IFormData,
  });

  const applyFormStore = useApplyFormStore();

  const [openModal, setOpenModal] = useState(false);
  const [, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const location = useLocation();

  useEffect(() => {
    if (location.state?.selectedCard) {
      formControl.setValue('desiredProduct', location.state.selectedCard?.name || '');
      window.scrollTo({ top: 750, behavior: 'smooth' });
    }
  }, [location]);

  useEffect(() => {
    setLoading(false);
    if (applyFormStore.success) setOpenModal(true);
    applyFormStore.clear();
  }, [applyFormStore.success]);

  useEffect(() => {
    setLoading(false);
    if (applyFormStore.error !== null && applyFormStore.error !== undefined) {
      setError(true);
    }
  }, [applyFormStore.error]);

  const onSubmit = (data: IFormData) => {
    if (data.annualIncome) {
      data.annualIncome = Number(data.annualIncome);
    }
    setLoading(true);
    applyFormStore.applyForm(data as ApplyCardRequest);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    formControl.reset({});
  };

  return (
    <PageLayout>
      <div className='bg-[#fff] pb-[161px] max-md:bg-[#fafafa] max-md:p-0'>
        <form
          className='bg-[#fff] max-md:relative max-md:mb-[56px] max-md:bg-[#fafafa] max-md:pb-[1px] max-md:after:absolute max-md:after:right-[16px] max-md:after:bottom-0 max-md:after:left-[16px] max-md:after:border max-md:after:border-[#0f0f11]'
          onSubmit={formControl.handleSubmit(onSubmit)}
        >
          <div
            className='flex justify-between p-0 max-md:block max-md:bg-size-[100%_auto] max-md:bg-top max-md:bg-no-repeat max-md:pt-[100%] md:!bg-none'
            style={{ backgroundImage: `url(${bgImg})` }}
          >
            <div className='mt-[191px] flex max-md:m-0 max-md:block max-md:pt-6'>
              <TitlePage
                title='Eligibility Form'
                subtitle='Are you ready to connect to our world? Check to see if you are eligible for additional Insignia services.'
              />
            </div>
            <img className='max-md:hidden' src={cartImg} alt='cart' />
          </div>
          <div className='mt-[50px] mr-[118px] ml-[161px] grid grid-cols-2 gap-x-12 gap-y-2 max-md:relative max-md:mx-4 max-md:mb-[35px] max-md:flex max-md:flex-col max-md:gap-y-4 max-md:bg-[#fff] max-md:p-[21px] max-md:pb-[92px]'>
            <Controller
              name='firstName'
              control={formControl.control}
              rules={{
                required: 'This field is required',
                maxLength: 100,
              }}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <Input label='First Name' isError={!!error} value={value} onChange={onChange} required />
              )}
            />
            <Controller
              name='lastName'
              control={formControl.control}
              rules={{
                required: 'This field is required',
                maxLength: 100,
              }}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <Input label='Last Name' isError={!!error} value={value} onChange={onChange} required />
              )}
            />
            <Controller
              name='birthDate'
              control={formControl.control}
              rules={{
                required: 'This field is required',
                maxLength: 100,
              }}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <Input label='Date of Birth' isError={!!error} type='date' value={value} onChange={onChange} />
              )}
            />
            <Controller
              name='address'
              control={formControl.control}
              rules={{
                required: 'This field is required',
                maxLength: 100,
              }}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <Input label='Residential Address' isError={!!error} value={value} onChange={onChange} />
              )}
            />
            <Controller
              name='nationality'
              control={formControl.control}
              rules={{
                required: 'This field is required',
                maxLength: 100,
              }}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <Input label='Nationality' isError={!!error} value={value} onChange={onChange} />
              )}
            />
            <Controller
              name='passportIssued'
              control={formControl.control}
              rules={{
                required: 'This field is required',
                maxLength: 100,
              }}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <Input label='Passport Country of Issuance' isError={!!error} value={value} onChange={onChange} />
              )}
            />
            <Controller
              name='employmentType'
              control={formControl.control}
              rules={{
                required: 'This field is required',
                maxLength: 100,
              }}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <Input label='Occupation Type' isError={!!error} value={value} onChange={onChange} />
              )}
            />
            <Controller
              name='companyName'
              control={formControl.control}
              rules={{
                required: 'This field is required',
                maxLength: 100,
              }}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <Input label='Company Name' isError={!!error} value={value} onChange={onChange} />
              )}
            />
            <Controller
              name='annualIncome'
              control={formControl.control}
              rules={{
                required: 'This field is required',
                maxLength: 100,
              }}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <Input label='Annual Income' isError={!!error} value={value} onChange={onChange} type='number' />
              )}
            />
            <Controller
              name='mobileNumber'
              control={formControl.control}
              rules={{
                required: 'This field is required',
                maxLength: 100,
              }}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <Input label='Mobile Number (including country code)' isError={!!error} value={value} onChange={onChange} />
              )}
            />
            <Controller
              name='email'
              control={formControl.control}
              rules={{
                required: 'This field is required',
                maxLength: 100,
                pattern:
                  // eslint-disable-next-line no-control-regex
                  /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/,
                // validate: (value) => {
                //   const isEmailValid = HelpersUtils.validateEmail(value);
                //   return isEmailValid ? undefined : 'Enter valid email address';
                // },
                // static validateEmail = (email: string) => {
                // return !!email.match(
                // eslint-disable-next-line no-control-regex
                // /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/,
                // );
                // };
              }}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <Input label='Email Address' isError={!!error} value={value} onChange={onChange} />
              )}
            />
            <Controller
              name='desiredProduct'
              control={formControl.control}
              rules={{
                required: 'This field is required',
                maxLength: 100,
              }}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <Input label='Desired Product' isError={!!error} value={value} onChange={onChange} />
              )}
            />
            <div className='max-md:absolute max-md:right-[21px] max-md:bottom-[21px] max-md:left-[21px]'>
              <Button className='mt-12 w-[282px] max-md:w-full' isLoading={loading} onClick={formControl.handleSubmit(onSubmit)}>
                Enquire Now
              </Button>
              {applyFormStore.error && (
                <div className='ml-2 flex items-baseline gap-1' style={{ marginTop: 25, marginLeft: 0 }}>
                  <div className='h-[13px] w-[12px] rounded-full bg-[#d0021b] text-center text-xs font-bold text-[#fff]'>!</div>
                  <div className='text-xs font-bold text-[#d0021b] uppercase'>
                    SOMETHING WENT WRONG. PLEASE TRY AGAIN OR CONTACT CUSTOMER SERVICE.
                  </div>
                </div>
              )}
            </div>
            <Controller
              name='jewelleryCardName'
              control={formControl.control}
              rules={{
                required: 'This field is required',
                maxLength: 100,
              }}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <Input label='Jewellery Card Name' isError={!!error} value={value} onChange={onChange} />
              )}
            />
            <Modal title='Thank you for your enquiry' isOpen={openModal} onClose={handleCloseModal}>
              <p className='mb-8 max-md:mb-6 max-md:text-[#4c4c4c80]'>An Insignia representative will contact you shortly.</p>
              <Button className='mx-auto w-[159px] max-md:w-full' type='submit' onClick={handleCloseModal}>
                Ok
              </Button>
            </Modal>
          </div>
          <div className='mt-[95px] ml-[161px] w-[80.6%] max-md:hidden' />
        </form>
        <div className='mx-auto h-[1px] w-[85%] bg-[#6c6c6c] max-md:hidden' />
        <InsigniaCards />
      </div>
    </PageLayout>
  );
};
